import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { ActiveArrowIcons } from 'src/components/common/ActiveArrowIcon'
import styled, { css, RuleSet } from 'styled-components'
import { Noto_body1_b16, Noto_body2_b14, Noto_body2_r14 } from 'src/styles/fonts/NotoSansKrFonts'
import RenderIf from 'src/components/common/RenderIf'
import { Container } from 'src/components/common/Container'
import { motion, useAnimationControls } from 'framer-motion'

type AccordionType = 'big' | 'middle'

interface AccordionProps {
  Title?: string
  Contents?: string
  children?: ReactNode
  TitleStyle?: RuleSet<object>
  type: AccordionType
  //default: active 설정
  defaultStatus?: boolean
  // active 후 스크롤 이동 여부
}

const Accordion = ({
  Contents,
  Title,
  children,
  TitleStyle,
  type,
  defaultStatus,
}: AccordionProps) => {
  const spiderRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState<boolean>(false)
  const toggleAccordion = () => setActive((prev) => !prev)
  const accordionControl = useAnimationControls()

  const handleStoreCardAnimation = {
    initial: { display: 'none', height: 'auto' },
    open: () => {
      accordionControl?.start({ display: 'flex', height: '100%', transition: { duration: 0.2 } })
      toggleAccordion()
    },
    close: () => {
      accordionControl?.start({ display: 'none', height: '0', transition: { duration: 0.2 } })
      toggleAccordion()
    },
  }

  const handleAccordionToggle = () =>
    active ? handleStoreCardAnimation?.close() : handleStoreCardAnimation?.open()

  useEffect(() => {
    if (defaultStatus) {
      const timerId = setTimeout(() => {
        accordionControl && handleAccordionToggle()
      }, 1000)

      return () => clearTimeout(timerId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultStatus])

  return (
    <AccordionWrap $option="flex_column">
      <AccordionInner onClick={handleAccordionToggle} $type={type} ref={spiderRef}>
        <AccordionTitle $style={TitleStyle}>{Title}</AccordionTitle>
        <ActiveArrowIcons $active={active} />
      </AccordionInner>
      <AccordionContentWrap animate={accordionControl} initial={handleStoreCardAnimation.initial}>
        <RenderIf when={!!Contents}>
          <AccordionContents dangerouslySetInnerHTML={{ __html: Contents as string }} />
        </RenderIf>
        <RenderIf when={!!children}>{children}</RenderIf>
      </AccordionContentWrap>
    </AccordionWrap>
  )
}
export default Accordion

const AccordionWrap = styled(Container)``

const AccordionInner = styled.div<{ $type: AccordionType }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  background-color: white;
  cursor: pointer;
  box-sizing: border-box;
  padding: 18px 20px;
  transition: background-color 0.2s ease-in-out;
  ${(props) =>
    props.$type === 'big' &&
    css`
      padding: 18px 20px;
    `}
  ${(props) =>
    props.$type === 'middle' &&
    css`
      border-top: 1px solid var(--black-6);
      padding: 12px 20px;
    `}
`

const AccordionContentWrap = styled(motion.div)`
  width: 100%;
  background-color: var(--white);
  overflow: hidden;
  height: auto;
`

const AccordionContents = styled.p`
  ${Noto_body2_r14};
  padding: 20px;
  background-color: var(--black-2);
  color: var(--black-70);
  width: 100%;
  tr {
    border: 1px solid var(--black-10);
    padding: 0;
  }

  td {
    border: 1px solid var(--black-10);
    padding: 10px 12px;
  }

  th {
    color: var(--black-60);
    ${Noto_body2_b14};
    text-align: left;
    padding: 10px 12px;
    background-color: var(--black-6);
  }

  table {
    border-radius: 4px;
    border: 1px solid var(--black-10);
    background: #fff;
  }
`

const AccordionTitle = styled.strong<{ $style?: RuleSet<Object> }>`
  max-width: calc(100% - 34px);
  ${Noto_body1_b16};
  ${(props) => props.$style};
`
